/**
 *
 * Login
 *
 */
import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { Input, Form, Checkbox, Button, Card, Row } from 'antd';
import { TextSemibold20 } from 'styles/typography';
import { UserOutlined, UnlockOutlined } from '@ant-design/icons';
import { RouteConstants } from '../../../../routes';
import { Link } from 'react-router-dom';
import auth0 from 'auth0-js';
import { API_URL, AUTH0_CLIENT_ID, AUTH0_DOMAIN } from 'utils/constants';
import {
  ILoginFormValues,
  ILoginOptions,
  loginFormEnum,
} from '../../slice/types';
import {
  useAddToHomescreenPrompt,
  useIsPWAInstalled,
} from 'utils/hooks/useAddToHomeScreenPrompt';

interface IProps {}
const LOGIN_FORM_ID = 'loginForm';

const Login = (props: IProps) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const [form] = Form.useForm();

  const { primary } = useTheme();

  const handleLoginSubmit = ({ email, password }: ILoginFormValues) => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    const webAuth = new auth0.WebAuth({
      domain: AUTH0_DOMAIN as string,
      clientID: AUTH0_CLIENT_ID as string,
    });
    const { origin } = window.location;
    const redirectUri = `${API_URL}/auth/authenticate?redirectUri=${origin}/auth/authenticate`;

    const options: ILoginOptions = {
      connection: 'Username-Password-Authentication',
      username: email,
      password,
      scope: 'openid email',
      redirectUri,
      responseType: 'code',
      responseMode: 'query',
    };

    webAuth.redirect.loginWithCredentials(options, error => {
      if (error?.error_description === 'Wrong email or password.') {
        form.setFields([
          {
            name: loginFormEnum.email,
            errors: [''],
          },
          {
            name: loginFormEnum.password,
            errors: [
              t('Auth.Login.WrongEmailOrPassword', 'Wrong email or password.'),
            ],
          },
        ]);
      }

      console.log('AUTH0 ERRORS:', error);
      setIsLoading(false);
    });
  };
  const { promptToInstall } = useAddToHomescreenPrompt();
  const { isAppInstalled } = useIsPWAInstalled();

  return (
    <LoginCard
      bordered={false}
      title={<TextSemibold20>{t('Auth.Login.Title', 'Login')}</TextSemibold20>}
      headStyle={{ borderBottom: 'none', textAlign: 'center' }}
    >
      <Form
        name="login"
        form={form}
        id={LOGIN_FORM_ID}
        onFinish={handleLoginSubmit}
        initialValues={{ rememberMe: false }}
      >
        <Form.Item name={loginFormEnum.email} rules={[{ required: true }]}>
          <Input
            name="email"
            placeholder={t('Auth.Login.Email', 'Email')}
            size="large"
            prefix={<UserOutlined style={{ color: primary }} />}
          />
        </Form.Item>

        <Form.Item name={loginFormEnum.password} rules={[{ required: true }]}>
          <Input.Password
            name="password"
            placeholder={t('Auth.Login.Password', 'Password')}
            size="large"
            prefix={<UnlockOutlined style={{ color: primary }} />}
          />
        </Form.Item>
        <Form.Item name={loginFormEnum.rememberMe} valuePropName="checked">
          <Row justify="space-between">
            <Checkbox>{t('Auth.Login.RememberMe', 'Remember me')}</Checkbox>
            <Link to={RouteConstants.auth.resetPassword}>
              {t('Auth.Login.ForgotPassword', 'Forgot password')}
            </Link>
          </Row>
        </Form.Item>
        <Form.Item>
          <Button
            block
            type="primary"
            htmlType="submit"
            size="large"
            loading={isLoading}
          >
            {t('Auth.Login.LogIn', 'Log in')}
          </Button>
        </Form.Item>
      </Form>

      {!isAppInstalled ? (
        <Button style={{ width: '100%' }} type="link" onClick={promptToInstall}>
          Add to Home Screen
        </Button>
      ) : (
        <div>Thanks for installing our app</div>
      )}
      {/* <Row justify="center" align="middle">
        <TextRegular14>{t('Auth.Login.NewUser', 'New User? ')}</TextRegular14>
        <Link to={RouteConstants.auth.registration}>
          <Button type="link">
            {t('Auth.Login.RegisterHere', 'Register Here.')}
          </Button>
        </Link>
      </Row> */}
    </LoginCard>
  );
};

const LoginCard = styled(Card)`
  width: 330px;
`;

export default Login;
